import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { ProviderContext, withSnackbar } from 'notistack';
import { InputLabel, TextField, Modal, Backdrop } from '@material-ui/core';

import { snackbar } from '../../services/util/snackbar';
import Checkbox from './checkbox';
import SelectCustom, { Props as CustomSelectProps } from './select';
import { RadioUnchecked, RadioChecked, CloseWhite } from '../../assets/svg';

import { ContactUsFormData } from '../../models/dataTypes/formData';

import Button from '../button/button';

import * as styles from './form.module.scss';

interface Fields {
    type: 'text' | 'longText' | 'dropdown' | 'radio' | 'checkbox',
    size: 'half' | 'halfRight' | 'halfLeft' | 'full',
    label?: string | null,
    fieldName: string,
    required: boolean,
    options?: string[] | 
        {
            option: string,
            value: string
        }[] |
        {
            id: string | number,
            label: string
        }[] |
        ((input: string) => {
            option: string,
            value: string
        }[]) |
        ((input: string) => {
            id: string | number,
            label: string
        }[]),
    indentOptions?: boolean,
    disabled?: boolean,
    multiSelect?: boolean,
    placeholder?: string,
    defaultValue? : string | boolean | string[],
    dependentField?: Fields,
    validator?: (input: string | boolean | string[] | null) => boolean,
}

interface PropType {
    eventName?: string;
    formTitle?: string;
    formSubtitle?: string;
    fields: Fields[],
    disclaimer?: {
        content: string,
        position?: 'beforeButton' | 'afterButton',
    } | undefined;
    submitFunc: (formData: FormData | ContactUsFormData) => [boolean, any] | Promise<[boolean, any]>,
    submitButton?: string,
    confirmation?: {
        title: string,
        content: string,
        image?: string,
        placement?: 'inForm' | 'modal',
        button?: string,
        closeFunc?: () => void,
    } | undefined;
    closeFunc?: () => void,
    usingMouse?: boolean;
    isMobile?: boolean;
}

export interface StateType {
    invalidFields: string[],
    requiredFields: string[],
    submitted: boolean,
    confirmationModalOpen: boolean,
    loading: boolean,
    eventName: string
}

class GenericForm extends React.Component<PropType & ProviderContext, StateType> {
    constructor(props: PropType & ProviderContext) {
        super(props);
        this.state = {
            invalidFields: [],
            requiredFields: [],
            submitted: false,
            confirmationModalOpen: false,
            loading: false,
            eventName: props.eventName ? props.eventName : 'formSubmission'
        };
    }

    componentDidMount() {
        this.setAllFieldStates();
    }

    async setAllFieldStates () {
        if (this.props.fields) {
            const reqd: string[] = [];
            await this.props.fields.forEach((field) => {
                if (field.defaultValue || field.defaultValue === false) {
                    this.setState({
                        ...this.state, 
                        [(field.fieldName)]: field.defaultValue
                    });
                } else {
                    this.setState({
                        ...this.state,
                        [field.fieldName]: ''
                    });
                }
                if (field.required) {
                    reqd.push(field.fieldName);
                }
            });
            this.setState({
                requiredFields: reqd
            });
        }
    }

    handleChange (val: string | boolean | string[], state: string) {
        this.setState(({
            [state]: val
        } as unknown) as StateType);
    }

    validateInputs(input: string | boolean | string[], validate: ((input: string | boolean | string[]) => boolean) | undefined, fieldName: string) {
        const currentInvalidList: string[] = this.state.invalidFields.slice();
        const invalidFieldInList: boolean = currentInvalidList.includes(fieldName);
        if (validate !== undefined && validate(input)) {
            if (invalidFieldInList) {
                this.setState({
                    invalidFields: currentInvalidList.filter((field) => {return field !== fieldName;})
                });
            }
        } else {
            if (!invalidFieldInList && this.state.requiredFields.includes(fieldName)) {
                currentInvalidList.push(fieldName);
                this.setState({
                    invalidFields: currentInvalidList
                });
            }
        }
    }

    isSelectedOption (fieldName: string, multiSelect: boolean, input: string) {
        const field = (this.state as any)[fieldName];
        if (field) {
            return multiSelect ? 
                field.includes(input) 
                    ? true 
                    : false 
                : true;
        }
        return false;
    }

    includesError (type: 'helper' | 'style', value: string, inputType: string) {
        if (this.state.invalidFields.includes(value)) {
            if (this.state.requiredFields.includes(value)) {
                if (inputType == 'text') {
                    return (
                        type == 'helper' 
                            ? <p className={styles.errorMessage}>{`Please enter a valid ${value.replace( /([A-Z])/g, ' $1' ).toLowerCase()}`}</p>
                            : true
                    );
                } else if (inputType == 'select') {
                    return (
                        type == 'helper' 
                            ? <p className={styles.errorMessage}>{'Please select a valid option'}</p>
                            : true
                    );
                } else if (inputType == 'agreement') {
                    return (
                        type == 'helper' 
                            ? <p className={styles.errorMessage}>{'Please check this box to continue.'}</p>
                            : true
                    );
                }
            }
        } else {
            return false;
        }
    }


    clearForm = (props: PropType['fields']) => {
        if (this.state.submitted) {
            props.map((field) => { 
                if (field.defaultValue || field.defaultValue === false) {
                    return (this.state as any)[field.fieldName] = field.defaultValue;
                } else {
                    return (this.state as any)[field.fieldName] = '';
                }
            });
        }
    }

    closeConfirmation () {
        if (!this.props.confirmation?.closeFunc && this.props.closeFunc) {
            this.props.closeFunc();
        } else if (this.props.confirmation?.closeFunc) {
            this.props.confirmation.closeFunc();
            this.setAllFieldStates();
        } 
    }

    loadingState () {
        if (this.state.loading) {
            return (
                <Button
                    label=' '
                    usingMouse={this.props.usingMouse}
                    spinner={true}
                    block='halfWithMargin'
                    size='lg'
                    isMobile={this.props.isMobile}
                />
            );
        } else {
            return (
                <Button
                    label={this.props.submitButton ? this.props.submitButton : 'Submit'}
                    onClick={() => this.submitForm()}
                    usingMouse={this.props.usingMouse}
                    block='halfWithMargin'
                    size='lg'
                    fullWidthOnMobile={true}
                    isMobile={this.props.isMobile}
                />
            );
        }
    }

    async submitForm () {
        const removeableFields: string[] = ['invalidFields', 'submitted', 'confirmationModalOpen', 'loading', 'requiredFields'];
        const invalids: string[] = this.state.invalidFields.slice();
        await this.state.requiredFields.forEach((field, i) => {
            if (!(this.state as any)[field]) {
                this.setState({
                    ...this.state, 
                    [field]: ''
                }, () => {
                    invalids.push(field);
                    if (i == this.state.requiredFields.length-1) {
                        this.setState({
                            invalidFields: invalids
                        });
                    }  
                });
            }
        });
        if (this.state.invalidFields.length > 0) {
            snackbar(
                this.props.enqueueSnackbar,
                'Seems like there is an error in your submission. Please review your information.',
                'error'
            );
        } else {
            const formEntries = JSON.parse(JSON.stringify(this.state));
            for (const key in formEntries) {
                if (removeableFields.includes(key)) {
                    delete formEntries[key];
                }
                this.state.requiredFields;
                if (this.state.requiredFields.includes(key) && (formEntries[key] == '' || formEntries[key].length == 0 || formEntries[key] == undefined)) {
                    snackbar(
                        this.props.enqueueSnackbar,
                        'Seems like there is an error in your submission. Please review your information.',
                        'error'
                    );
                }
            }
            if (!this.state.invalidFields.length) {
                this.setState({ loading: true });
                const response: [boolean, any] = await this.props.submitFunc(formEntries);
                if (response[0]) {
                    this.setState({
                        submitted: true,
                        loading: false,
                        confirmationModalOpen: !this.state.confirmationModalOpen
                    });
                    await this.clearForm(this.props.fields);
                } else {
                    this.setState({ loading: false });
                    snackbar(
                        this.props.enqueueSnackbar,
                        'There was an error in your submission. Please try again later.',
                        'error'
                    );
                }
            }
        }
    }

    render() {
        return (
            <div className={styles.formContainer}>
                <div className={styles.form}>
                    {
                        this.props.formTitle || this.props.formSubtitle
                            ? (
                                <Row noGutters className={`${styles.formRow} ${styles.formTitle}`}>
                                    {this.props.formTitle 
                                        ? <h2 className={styles.title} >{this.props.formTitle}</h2>
                                        : null
                                }
                                    {this.props.formSubtitle 
                                        ? <p>{this.props.formSubtitle}</p>
                                        : null
                                }
                                </Row>
                            ) : null
                    }
                    <Modal
                        className={styles.confirmationModal}
                        aria-labelledby='contained-modal-title-vcenter'
                        open={this.state.submitted && this.state.confirmationModalOpen}
                        onClose={() => {
                            this.setState({
                                confirmationModalOpen: false
                            }, () => this.closeConfirmation());
                        }}
                        closeAfterTransition
                        BackdropComponent={Backdrop}
                        BackdropProps={{
                            timeout: 500
                        }}>
                        <div className={styles.confirmationContent}>
                            <Col lg={12} className={styles.closeButtons}>
                                <h2 id='contained-modal-title-vcenter'>
                                    {this.props.confirmation?.title}
                                </h2>
                                <div>
                                    <CloseWhite
                                        className={styles.closeIcon}
                                        onClick={() => {
                                            this.setState({
                                                confirmationModalOpen: false
                                            }, () => this.closeConfirmation());
                                        }}
                                    />
                                </div>
                            </Col>
                            <div className={styles.content}> 
                                {this.props.confirmation?.content}
                            </div>
                            <div className={styles.confirmButtonContainer}>
                                <Button 
                                    usingMouse={this.props.usingMouse}
                                    label={this.props.confirmation?.button ? this.props.confirmation?.button : 'Close'}
                                    size='lg'
                                    onClick={() => {
                                        this.setState({
                                            confirmationModalOpen: false
                                        }, () => {this.closeConfirmation();});
                                    }}
                                    isMobile={this.props.isMobile}
                                />
                            </div>
                        </div>
                    </Modal>
                    <Row noGutters className={this.state.invalidFields.length ? styles.showErrorHeader : styles.hideErrorHeader}>
                        <p className='c1'>
                            Please review the following fields.
                        </p>
                    </Row>
                    <Row noGutters className={styles.formRow}>
                        {
                            this.props.fields.map((field, i) => {
                                const fields = (this.state as any)[field.fieldName];
                                const fieldStyles = (styles as any)[field.fieldName];
                                if (field.type === 'text' || field.type === 'longText') {
                                    return (
                                        <Col 
                                            sm={12} 
                                            lg={field.size === 'full' ? 12 : 6} 
                                            className={`${styles.inputText} ${(styles as any)[field.fieldName]} ${field.size === 'halfRight' ? styles.halfRight : field.size === 'halfLeft' ? styles.halfLeft : ''}`}
                                            key={i}
                                        >
                                            <InputLabel required={field.required} >{field.label}</InputLabel>
                                            <TextField
                                                name={field.fieldName}
                                                value={(this.state as any)[field.fieldName] || ''}
                                                onChange={(e: React.FormEvent<HTMLInputElement> | any) => {
                                                    this.handleChange(e.currentTarget.value, field.fieldName);
                                                }}
                                                onBlur={field.validator
                                                    ? () => this.validateInputs(fields, field.validator, field.fieldName)
                                                    : () => null}
                                                id={`outlined-basic ${field.fieldName}`}
                                                variant='outlined'
                                                fullWidth
                                                margin='dense'
                                                multiline={field.type === 'longText' ? true : false}
                                                rows={field.type === 'longText' ? 2 : 1}
                                                rowsMax={field.type === 'longText' ? 4 : 1}
                                                className={
                                                    this.includesError('style', field.fieldName, 'text') && this.state.requiredFields.includes(field.fieldName)
                                                        ? styles.inputError
                                                        : ''
                                                }
                                            />
                                            {this.includesError('helper', field.fieldName, 'text')}
                                        </Col>
                                    );
                                } else if (field.type === 'dropdown' && field.options) {
                                    if (field.options.length) {
                                        return (
                                            <React.Fragment key={`dropdown-${i}`}>
                                                <Col
                                                    sm={12} 
                                                    lg={field.size === 'full' ? 12 : 6} 
                                                    className={fieldStyles}
                                            >
                                                    {
                                                    field.label 
                                                        ? <InputLabel required={field.required} >{field.label}</InputLabel>
                                                        : null
                                                }
                                                    <div
                                                        className={`${styles.dropdown} 
                                                        ${this.includesError('style', field.fieldName, 'select')
                                                            ? styles.dropdownError
                                                            : ''}`
                                                    }
                                                >
                                                        <SelectCustom
                                                            onSelect={(e: any) => {
                                                                this.handleChange(e.currentTarget.innerHTML, field.fieldName);
                                                                field.dependentField 
                                                                    ? this.handleChange('', field.dependentField.fieldName)
                                                                    : null;
                                                                this.validateInputs(e.currentTarget.innerHTML, field.validator, field.fieldName);
                                                            }}
                                                            label={field.placeholder ? field.placeholder : ''}
                                                            selected={fields}
                                                            usingMouse={this.props.usingMouse}
                                                            menuItems={field.options as unknown as CustomSelectProps['menuItems']}
                                                            styles={{ labelFontClass: 'c1' }}
                                                            labelDisplay='showSelectedInLabel'
                                                            disabled={field.disabled ? field.disabled : false}
                                                    />
                                                        {this.includesError('helper', field.fieldName, 'select')}
                                                    </div>
                                                </Col>
                                                {
                                                    fields
                                                    && field.dependentField
                                                    && (
                                                        typeof field.dependentField?.options === 'function' && field.dependentField.options(fields).length
                                                    ) || (
                                                        typeof field.dependentField?.options == 'object' && field.dependentField?.options.length
                                                    )
                                                        ? (
                                                            <Col
                                                                sm={12} 
                                                                lg={field.size === 'full' ? 12 : 6} 
                                                                className={`${(styles as any)[field.fieldName]}`}
                                                                key={`dependent-${i}`}
                                                            >
                                                                {
                                                                    field.dependentField.label 
                                                                        ? <InputLabel required={field.dependentField.required} >{field.dependentField.label}</InputLabel>
                                                                        : null
                                                                }
                                                                <div>
                                                                    <SelectCustom
                                                                        onSelect={(e: any) => {
                                                                            this.handleChange(e.currentTarget.innerHTML, field.dependentField.fieldName);
                                                                        }}
                                                                        label={field.dependentField.placeholder ? field.dependentField.placeholder : ''}
                                                                        selected={(this.state as any)[field.dependentField.fieldName]}
                                                                        usingMouse={this.props.usingMouse}
                                                                        menuItems={
                                                                            (typeof field.dependentField?.options === 'function'
                                                                                ? field.dependentField.options(fields)
                                                                                : field.dependentField.options) as unknown as CustomSelectProps['menuItems']
                                                                        }
                                                                        styles={{ labelFontClass: 'c1' }}
                                                                        labelDisplay='showSelectedInLabel'
                                                                    />
                                                                    {this.includesError('helper', field.dependentField.fieldName, 'select')}
                                                                </div>
                                                            </Col>
                                                        )
                                                        : null
                                                }

                                            </React.Fragment>
                                        );
                                    }
                                    
                                } else if (field.type === 'checkbox') {
                                    if (!field.multiSelect) {
                                        return (
                                            <Col
                                                sm={12} 
                                                lg={field.size === 'full' ? 12 : 6} 
                                                className={`${(styles as any)[field.fieldName]}`}
                                                key={i}
                                            >
                                                {
                                                    field.label 
                                                        ? <InputLabel required={field.required} >{field.label}</InputLabel>
                                                        : null
                                                }
                                                {typeof field.options == 'object' && field.options.length ? field.options?.map((item: any, a: number) => {
                                                    return (
                                                        <div
                                                            role='checkbox'
                                                            aria-labelledby='optin-checkbox'
                                                            key={a}
                                                            className={
                                                                this.includesError('style', field.fieldName, 'select')
                                                                    ? styles.inputError
                                                                    : ''
                                                            }
                                                            >
                                                            <div
                                                                className={
                                                                    this.props.usingMouse
                                                                        ? `using-mouse ${styles.checkboxOptions} ${field.indentOptions ? 'indentedOptions' : null}`
                                                                        : styles.checkboxOptions
                                                                }
                                                                style={field.indentOptions ? {marginLeft: '10px'} : {}}
                                                                >
                                                                <Checkbox
                                                                    label={item}
                                                                    usingMouse={this.props.usingMouse}
                                                                    onClick={async () => {
                                                                        await this.handleChange(!fields, field.fieldName);
                                                                        this.validateInputs((this.state as any)[field.fieldName], field.validator, field.fieldName);
                                                                    }}
                                                                    isSelected={this.isSelectedOption(field.fieldName, false, item)}
                                                                    id={`option-${item.split(' ').join('-')}`}
                                                                    checkmark={true}
                                                                />
                                                            </div>
                                                            {field.required ? this.includesError('helper', field.fieldName, 'agreement') : this.includesError('helper', field.fieldName, 'select')}
                                                        </div>
                                                    );
                                                }) : null}
                                            </Col>
                                        );
                                    } else if (field.multiSelect) {
                                        return (
                                            <Col
                                                sm={12} 
                                                lg={field.size === 'full' ? 12 : 6} 
                                                className={
                                                    this.includesError('style', field.fieldName, 'select')
                                                        ? styles.inputError
                                                        : ''
                                                }
                                                key={i}
                                            >
                                                {
                                                    field.label 
                                                        ? <InputLabel required={field.required} >{field.label}</InputLabel>
                                                        : null
                                                }
                                                {typeof field.options == 'object' && field.options.length ? field.options.map((item: any, j: number) => {
                                                    return (
                                                        <div
                                                            role='checkbox'
                                                            aria-labelledby='optin-checkbox'
                                                            key={j}
                                                            >
                                                            <div
                                                                className={
                                                                    this.props.usingMouse
                                                                        ? `using-mouse ${styles.checkboxOptions} ${field.indentOptions ? 'indentedOptions' : null}`
                                                                        : styles.checkboxOptions
                                                                }
                                                                style={field.indentOptions ? {marginLeft: '10px'} : {}}
                                                                >
                                                                <Checkbox
                                                                    label={item}
                                                                    usingMouse={this.props.usingMouse}
                                                                    onClick={() => {
                                                                        const tempArr = fields.slice();
                                                                        if (fields.includes(item)) {
                                                                            const i = tempArr.indexOf(item);
                                                                            tempArr.splice(i, 1);
                                                                        } else {
                                                                            tempArr.push(item);
                                                                        }
                                                                        this.validateInputs(tempArr, (input: string) => Boolean(input.length), field.fieldName);
                                                                        this.handleChange(tempArr, field.fieldName);
                                                                    }}
                                                                    isSelected={this.isSelectedOption(field.fieldName, true, item)}
                                                                    id={`option-${item.split(' ').join('-')}`}
                                                                    checkmark={true}
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                }): null}
                                                {this.includesError('helper', field.fieldName, 'select')}
                                            </Col>
                                        );
                                    }
                                } else if (field.type === 'radio') {
                                    return (
                                        <Col 
                                            sm={12} 
                                            lg={field.size === 'full' ? 12 : 6} 
                                            className={fieldStyles}
                                            key={i}
                                            >
                                            {
                                                field.label 
                                                    ? <InputLabel required={field.required} >{field.label}</InputLabel>
                                                    : null
                                            }
                                            <div
                                                role='radiogroup'
                                                aria-labelledby='cardStatus-radio'
                                            >
                                                <div
                                                    className={
                                                            this.includesError('style', field.fieldName, 'select')
                                                                ? styles.inputError
                                                                : ''
                                                        }
                                                        >
                                                    {typeof field.options == 'object' && field.options.length ? field.options.map((option: any, k: number) => {
                                                        return (
                                                            <div
                                                                className={
                                                                    this.props.usingMouse
                                                                        ? `c1 using-mouse ${styles.option}`
                                                                        : `c1 ${styles.option}`
                                                                }
                                                                key={k}
                                                                role='radio'
                                                                aria-label={option.id}
                                                                tabIndex={0}
                                                                aria-checked={
                                                                        fields === option.label
                                                                    }
                                                                onClick={() => {
                                                                    this.handleChange(option.label, field.fieldName);
                                                                    this.validateInputs(option.label, field.validator, field.fieldName);
                                                                }}
                                                                onKeyPress={(e: any) => {
                                                                    if (e.key === 'Enter') {
                                                                        this.handleChange(
                                                                            option.label,
                                                                            field.fieldName
                                                                        );
                                                                    }
                                                                }}
                                                            >
                                                                {fields === option.label ? (
                                                                    <RadioChecked
                                                                        aria-hidden='true'
                                                                        className={styles.option}
                                                                    />
                                                                ) : (
                                                                    <RadioUnchecked
                                                                        aria-hidden='true'
                                                                        className={styles.option}
                                                                        />
                                                                )
                                                                }
                                                                <label id={option.id} aria-hidden='true'>
                                                                    {option.label}
                                                                </label>
                                                            </div>
                                                        );
                                                    }) : null}
                                                    {this.includesError('helper', field.fieldName, 'select')}
                                                </div>
                                            </div>
                                        </Col>
                                    );
                                }
                            })
                        }
                        <Col lg={12}>
                            {
                                this.props.disclaimer
                                    ? (this.props.disclaimer.position == 'afterButton'
                                        ? <React.Fragment>
                                            {this.loadingState()}
                                            <br />
                                            <br />
                                            <p className='body2'>
                                                {this.props.disclaimer.content}
                                            </p>
                                        </React.Fragment>
                                        : <React.Fragment>
                                            <p className='body2'>
                                                {this.props.disclaimer.content}
                                            </p>
                                            <br />
                                            <br />
                                            {this.loadingState()}
                                        </React.Fragment>
                                    ): this.loadingState()
                            }
                        </Col>
                    </Row>
                </div>
            </div>
        );
    }
}

export default withSnackbar(GenericForm);